/**
 * The session: a stream of tutoring, with the exercise inside it.
 *
 * The exercise is not a separate screen. A child who has to leave the
 * conversation to answer loses the thread of what was just explained — and the
 * hint they were given two lines above.
 *
 * **« Exercices N réussis sur M » au lycée seulement** (§14.1, assoupli le
 * 29 septembre 2026 pour le lycée). Au collège le résumé garde le temps et les
 * objectifs, cochés ou non, et aucun rapport : un « 3 sur 5 » lu par une élève
 * de 5ᵉ est une note. Le compte reste dans l'événement de séance, que les
 * parents lisent ; c'est l'écran de l'enfant qui ne le dessine pas.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useSession } from "@/app/session";
import { useTutorSession } from "@/child/use-tutor-session";
import { ExercisePanel } from "@/child/ExercisePanel";
import { MilestoneBanner } from "@/child/MilestoneBanner";
import { warmComputeEngine } from "@/math/mathjson";
import { Button, Card, ErrorNote, Loading } from "@/ui/primitives";

export function SessionScreen() {
  const { profile } = useSession();
  const session = useTutorSession();
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    warmComputeEngine();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [session.entries.length]);

  if (!session.ready && !session.error) return <Loading label="J'ouvre la séance…" />;

  return (
    <main className="screen screen--session">
      {session.error ? <ErrorNote>{session.error}</ErrorNote> : null}
      <MilestoneBanner corrections={session.corrections} />

      <div className="chat" aria-live="polite">
        {session.entries.length === 0 ? (
          <p className="muted">
            {profile.tone === "encouraging"
              ? "Dis bonjour, ou pose ta question. Le tuteur suit."
              : "La séance est ouverte."}
          </p>
        ) : null}

        {session.entries.map((entry) => (
          <article key={entry.key} className={`bubble bubble--${entry.author}`} data-state={entry.state}>
            <p>{entry.text}</p>
            {entry.state === "queued" ? (
              <p className="bubble__note">en attente d'envoi — pas encore corrigé</p>
            ) : null}
            {entry.state === "failed" ? (
              <p className="bubble__note">
                Ton message n'est pas parti. Il est gardé ici.{" "}
                <button
                  type="button"
                  className="bubble__retry"
                  onClick={() => entry.clientId && void session.retry(entry.clientId)}
                >
                  Réessayer
                </button>
              </p>
            ) : null}
          </article>
        ))}
        {session.thinking ? (
          <article className="bubble bubble--tutor" data-state="thinking" aria-live="polite">
            <p className="sr-only">Le tuteur réfléchit.</p>
            <span className="dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </article>
        ) : null}
        <div ref={bottomRef} />
      </div>

      {session.exercise ? (
        <ExercisePanel exercise={session.exercise} hints={session.hints} />
      ) : null}

      {session.report ? (
        <Card title="Le résumé de la séance">
          <p className="muted small">
            C'est le tuteur qui l'écrit, à partir de ton travail. Tu ne peux pas le
            changer — mais tu le lis avant tes parents.
          </p>

          <dl className="tally">
            <div>
              <dt>Temps</dt>
              <dd>{session.report.minutes} min</dd>
            </div>
            {profile.ownNumbers ? (
              <div>
                <dt>Exercices</dt>
                <dd>
                  {session.report.exercises_passed} réussis sur {session.report.exercises_done}
                </dd>
              </div>
            ) : null}
          </dl>

          {(session.report.objectives ?? []).length > 0 ? (
            <ul className="objectives">
              {(session.report.objectives ?? []).map((objective) => (
                <li key={objective.competency_id} data-reached={objective.reached ? "true" : "false"}>
                  <span aria-hidden="true">{objective.reached ? "✓" : objective.worked ? "◐" : "○"}</span>
                  <span>
                    {objective.label}
                    <span className="sr-only">
                      {objective.reached
                        ? " : atteint"
                        : objective.worked
                          ? " : travaillé, pas encore acquis"
                          : " : pas travaillé aujourd'hui"}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          {session.report.text ? <p className="report__text">{session.report.text}</p> : null}

          <p className="muted small">
            {/* « Aujourd'hui » a disparu le 3 septembre 2026 avec la refonte autour
                du journal : le travail du soir est dans « Mon travail ». */}
            La séance est finie. Tu peux fermer, ou retrouver ton travail du soir dans{" "}
            <Link to="/eleve/travail">Mon travail</Link>.
          </p>
        </Card>
      ) : null}

      {session.report ? null : (
      <form
        className="composer"
        onSubmit={(event) => {
          event.preventDefault();
          const text = draft;
          setDraft("");
          void session.send(text);
        }}
      >
        <label className="sr-only" htmlFor="composer">
          Ton message
        </label>
        <textarea
          id="composer"
          className="composer__input"
          rows={2}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Écris ici"
        />
        <Button type="submit" disabled={!draft.trim()}>
          Envoyer
        </Button>
      </form>
      )}

      {session.report ? null : (
        <p className="finish">
          <Button
            variant="secondary"
            disabled={!session.ready || session.finishing}
            onClick={() => void session.finish()}
          >
            {session.finishing ? "Un instant…" : "Finir la séance"}
          </Button>
        </p>
      )}

      <p className="status-line">
        {session.status === "live"
          ? "Connecté"
          : session.status === "connecting"
            ? "Je me connecte…"
            : "Connexion perdue — je réessaie tout seul."}
      </p>
    </main>
  );
}
